import { forwardRef } from 'react';
import ReactSelect__default from 'react-select';
export * from 'react-select';
import { useTheme, useFormControl, useMultiStyleConfig, Box, useColorModeValue, MenuIcon, chakra, Menu as Menu$1, Spinner, Divider, useStyleConfig, useBreakpointValue, Icon } from '@chakra-ui/react';
import { jsx, jsxs } from 'react/jsx-runtime';
import CreatableReactSelect from 'react-select/creatable';
export { useCreatable } from 'react-select/creatable';
import AsyncReactSelect from 'react-select/async';
export { useAsync } from 'react-select/async';
import AsyncCreatableReactSelect from 'react-select/async-creatable';

// src/select/select.tsx
var cleanCommonProps = (props) => {
  const {
    className,
    // not listed in commonProps documentation, needs to be removed to allow Emotion to generate classNames
    clearValue,
    cx,
    getStyles,
    getClassNames,
    getValue,
    hasValue,
    isMulti,
    isRtl,
    options,
    // not listed in commonProps documentation
    selectOption,
    selectProps,
    setValue,
    theme,
    // not listed in commonProps documentation
    ...innerProps
  } = props;
  return { ...innerProps };
};
var isSize = (size) => {
  const isString = typeof size === "string";
  return isString && ["sm", "md", "lg"].includes(size);
};
var getDefaultSize = (size) => {
  if (isSize(size)) {
    return size;
  }
  if (size === "xs") {
    return "sm";
  }
  if (size === "xl") {
    return "lg";
  }
  return "md";
};
var useSize = (size) => {
  const chakraTheme = useTheme();
  const defaultSize = getDefaultSize(
    chakraTheme.components.Input.defaultProps.size
  );
  const definedSize = size != null ? size : defaultSize;
  const realSize = useBreakpointValue(
    typeof definedSize === "string" ? [definedSize] : definedSize,
    {
      fallback: "md"
    }
  ) || defaultSize;
  return realSize;
};
var SelectContainer = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    isDisabled,
    isRtl,
    hasValue,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    position: "relative",
    direction: isRtl ? "rtl" : void 0,
    ...isDisabled ? { cursor: "not-allowed" } : {}
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.container) ? chakraStyles.container(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          "--is-disabled": isDisabled,
          "--is-rtl": isRtl,
          "--has-value": hasValue
        },
        className
      ),
      sx,
      children
    }
  );
};
var ValueContainer = (props) => {
  const {
    children,
    className,
    cx,
    isMulti,
    hasValue,
    innerProps,
    selectProps: {
      chakraStyles,
      size: sizeProp,
      variant,
      focusBorderColor,
      errorBorderColor,
      controlShouldRenderValue
    }
  } = props;
  const size = useSize(sizeProp);
  const inputStyles = useMultiStyleConfig("Input", {
    size,
    variant,
    focusBorderColor,
    errorBorderColor
  });
  const initialSx = {
    display: isMulti && hasValue && controlShouldRenderValue ? "flex" : "grid",
    alignItems: "center",
    flex: 1,
    paddingY: "2px",
    paddingX: inputStyles.field.px,
    flexWrap: "wrap",
    WebkitOverflowScrolling: "touch",
    position: "relative",
    overflow: "hidden"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.valueContainer) ? chakraStyles.valueContainer(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          "value-container": true,
          "value-container--is-multi": isMulti,
          "value-container--has-value": hasValue
        },
        className
      ),
      sx,
      children
    }
  );
};
var IndicatorsContainer = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    flexShrink: 0
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.indicatorsContainer) ? chakraStyles.indicatorsContainer(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          indicators: true
        },
        className
      ),
      sx,
      children
    }
  );
};
var Control = (props) => {
  const {
    className,
    cx,
    children,
    innerRef,
    innerProps,
    isDisabled,
    isFocused,
    menuIsOpen,
    selectProps: {
      chakraStyles,
      size: sizeProp,
      variant,
      focusBorderColor,
      errorBorderColor,
      isInvalid,
      isReadOnly
    }
  } = props;
  const size = useSize(sizeProp);
  const {
    field: { height, h, ...fieldStyles }
  } = useMultiStyleConfig("Input", {
    size,
    variant,
    focusBorderColor,
    errorBorderColor
  });
  const minH = height || h;
  const initialSx = {
    ...fieldStyles,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    padding: 0,
    overflow: "hidden",
    height: "auto",
    minH,
    ...isDisabled ? { pointerEvents: "none" } : {}
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.control) ? chakraStyles.control(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ref: innerRef,
      className: cx(
        {
          control: true,
          "control--is-disabled": isDisabled,
          "control--is-focused": isFocused,
          "control--menu-is-open": menuIsOpen
        },
        className
      ),
      sx,
      ...innerProps,
      "data-focus": isFocused ? true : void 0,
      "data-focus-visible": isFocused ? true : void 0,
      "data-invalid": isInvalid ? true : void 0,
      "data-disabled": isDisabled ? true : void 0,
      "data-readonly": isReadOnly ? true : void 0,
      children
    }
  );
};
var IndicatorSeparator = (props) => {
  const {
    className,
    cx,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    opacity: 1,
    // To match the default styles of the Chakra select, we don't want to show the separator
    display: "none"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.indicatorSeparator) ? chakraStyles.indicatorSeparator(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Divider,
    {
      className: cx({ "indicator-separator": true }, className),
      sx,
      orientation: "vertical"
    }
  );
};
var DownChevron = (props) => /* @__PURE__ */ jsx(Icon, { role: "presentation", focusable: "false", "aria-hidden": "true", ...props, children: /* @__PURE__ */ jsx(
  "path",
  {
    fill: "currentColor",
    d: "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
  }
) });
var DropdownIndicator = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: {
      chakraStyles,
      size: sizeProp,
      focusBorderColor,
      errorBorderColor,
      variant
    }
  } = props;
  const size = useSize(sizeProp);
  const selectStyles = useMultiStyleConfig("Select", {
    size,
    variant,
    focusBorderColor,
    errorBorderColor
  });
  const initialDropdownIndicatorSx = {
    ...selectStyles.icon,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "static",
    marginRight: 2,
    marginLeft: 1,
    pointerEvents: "none"
  };
  const dropdownIndicatorSx = (chakraStyles == null ? void 0 : chakraStyles.dropdownIndicator) ? chakraStyles.dropdownIndicator(initialDropdownIndicatorSx, props) : initialDropdownIndicatorSx;
  const initialDownChevronSx = {
    height: "1em",
    width: "1em"
  };
  const downChevronSx = (chakraStyles == null ? void 0 : chakraStyles.downChevron) ? chakraStyles.downChevron(initialDownChevronSx, props) : initialDownChevronSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          indicator: true,
          "dropdown-indicator": true
        },
        className
      ),
      sx: dropdownIndicatorSx,
      children: children || /* @__PURE__ */ jsx(DownChevron, { sx: downChevronSx })
    }
  );
};
var CrossIcon = (props) => /* @__PURE__ */ jsx(Icon, { focusable: "false", "aria-hidden": true, ...props, children: /* @__PURE__ */ jsx(
  "path",
  {
    fill: "currentColor",
    d: "M.439,21.44a1.5,1.5,0,0,0,2.122,2.121L11.823,14.3a.25.25,0,0,1,.354,0l9.262,9.263a1.5,1.5,0,1,0,2.122-2.121L14.3,12.177a.25.25,0,0,1,0-.354l9.263-9.262A1.5,1.5,0,0,0,21.439.44L12.177,9.7a.25.25,0,0,1-.354,0L2.561.44A1.5,1.5,0,0,0,.439,2.561L9.7,11.823a.25.25,0,0,1,0,.354Z"
  }
) });
var ClearIndicator = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles, size: sizeProp }
  } = props;
  const size = useSize(sizeProp);
  const closeButtonStyles = useStyleConfig("CloseButton", {
    size
  });
  const initialSx = {
    ...closeButtonStyles,
    marginX: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    cursor: "pointer"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.clearIndicator) ? chakraStyles.clearIndicator(initialSx, props) : initialSx;
  const initialIconStyles = {
    width: "1em",
    height: "1em"
  };
  const iconSx = (chakraStyles == null ? void 0 : chakraStyles.crossIcon) ? chakraStyles.crossIcon(initialIconStyles, props) : initialIconStyles;
  return /* @__PURE__ */ jsx(
    Box,
    {
      role: "button",
      className: cx(
        {
          indicator: true,
          "clear-indicator": true
        },
        className
      ),
      sx,
      "aria-label": "Clear selected options",
      ...innerProps,
      children: children || /* @__PURE__ */ jsx(CrossIcon, { sx: iconSx })
    }
  );
};
var LoadingIndicator = (props) => {
  const {
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles, size: sizeProp },
    color,
    emptyColor,
    speed,
    thickness,
    spinnerSize: propsSpinnerSize
  } = props;
  const size = useSize(sizeProp);
  const spinnerSizes = {
    sm: "xs",
    md: "sm",
    lg: "md"
  };
  const spinnerSize = spinnerSizes[size];
  const initialSx = { marginRight: 3 };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.loadingIndicator) ? chakraStyles.loadingIndicator(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Spinner,
    {
      className: cx(
        {
          indicator: true,
          "loading-indicator": true
        },
        className
      ),
      sx,
      ...innerProps,
      size: propsSpinnerSize || spinnerSize,
      color,
      emptyColor,
      speed,
      thickness
    }
  );
};
var Input = (props) => {
  const {
    className,
    cx,
    value,
    selectProps: { chakraStyles, isReadOnly }
  } = props;
  const { innerRef, isDisabled, isHidden, inputClassName, ...innerProps } = cleanCommonProps(props);
  const spacingSx = {
    gridArea: "1 / 2",
    minW: "2px",
    border: 0,
    margin: 0,
    outline: 0,
    padding: 0
  };
  const initialContainerSx = {
    flex: "1 1 auto",
    display: "inline-grid",
    gridArea: "1 / 1 / 2 / 3",
    gridTemplateColumns: "0 min-content",
    color: "inherit",
    marginX: "0.125rem",
    paddingY: "0.125rem",
    visibility: isDisabled ? "hidden" : "visible",
    // Force css to recompute when value change due to @emotion bug.
    // We can remove it whenever the bug is fixed.
    transform: value ? "translateZ(0)" : "",
    _after: {
      content: 'attr(data-value) " "',
      visibility: "hidden",
      whiteSpace: "pre",
      padding: 0,
      ...spacingSx
    }
  };
  const containerSx = (chakraStyles == null ? void 0 : chakraStyles.inputContainer) ? chakraStyles.inputContainer(initialContainerSx, props) : initialContainerSx;
  const initialInputSx = {
    background: 0,
    opacity: isHidden ? 0 : 1,
    width: "100%",
    ...spacingSx
  };
  const inputSx = (chakraStyles == null ? void 0 : chakraStyles.input) ? chakraStyles.input(initialInputSx, props) : initialInputSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      className: cx({ "input-container": true }, className),
      "data-value": value || "",
      sx: containerSx,
      children: /* @__PURE__ */ jsx(
        chakra.input,
        {
          className: cx({ input: true }, inputClassName),
          ref: innerRef,
          sx: inputSx,
          disabled: isDisabled,
          readOnly: isReadOnly ? true : void 0,
          ...innerProps
        }
      )
    }
  );
};
var alignToControl = (placement) => {
  const placementToCSSProp = { bottom: "top", top: "bottom" };
  return placement ? placementToCSSProp[placement] : "top";
};
var Menu = (props) => {
  const {
    className,
    cx,
    children,
    innerProps,
    innerRef,
    placement,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    position: "absolute",
    [alignToControl(placement)]: "100%",
    marginY: "8px",
    width: "100%",
    zIndex: 1
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.menu) ? chakraStyles.menu(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(Menu$1, { children: /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      ref: innerRef,
      className: cx({ menu: true }, className),
      sx,
      children
    }
  ) });
};
var MenuList = (props) => {
  var _a;
  const {
    className,
    cx,
    innerRef,
    children,
    maxHeight,
    isMulti,
    innerProps,
    selectProps: {
      chakraStyles,
      size: sizeProp,
      variant,
      focusBorderColor,
      errorBorderColor
    }
  } = props;
  const menuStyles = useMultiStyleConfig("Menu");
  const size = useSize(sizeProp);
  const inputStyles = useMultiStyleConfig("Input", {
    size,
    variant,
    focusBorderColor,
    errorBorderColor
  });
  const fieldStyles = inputStyles.field;
  const initialSx = {
    ...menuStyles.list,
    minW: "100%",
    maxHeight: `${maxHeight}px`,
    overflowY: "auto",
    // This is hacky, but it works. May be removed in the future
    "--input-border-radius": fieldStyles == null ? void 0 : fieldStyles["--input-border-radius"],
    borderRadius: (fieldStyles == null ? void 0 : fieldStyles.borderRadius) || ((_a = menuStyles.list) == null ? void 0 : _a.borderRadius),
    position: "relative",
    // required for offset[Height, Top] > keyboard scroll
    WebkitOverflowScrolling: "touch"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.menuList) ? chakraStyles.menuList(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          "menu-list": true,
          "menu-list--is-multi": isMulti
        },
        className
      ),
      sx,
      ref: innerRef,
      children
    }
  );
};
var LoadingMessage = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles, size: sizeProp }
  } = props;
  const size = useSize(sizeProp);
  const verticalPaddings = {
    sm: "6px",
    md: "8px",
    lg: "10px"
  };
  const initialSx = {
    color: "chakra-subtle-text",
    textAlign: "center",
    paddingY: verticalPaddings[size],
    fontSize: size
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.loadingMessage) ? chakraStyles.loadingMessage(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          "menu-notice": true,
          "menu-notice--loading": true
        },
        className
      ),
      sx,
      children
    }
  );
};
var NoOptionsMessage = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles, size: sizeProp }
  } = props;
  const size = useSize(sizeProp);
  const verticalPaddings = {
    sm: "6px",
    md: "8px",
    lg: "10px"
  };
  const initialSx = {
    color: "chakra-subtle-text",
    textAlign: "center",
    paddingY: verticalPaddings[size],
    fontSize: size
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.noOptionsMessage) ? chakraStyles.noOptionsMessage(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          "menu-notice": true,
          "menu-notice--no-options": true
        },
        className
      ),
      sx,
      children
    }
  );
};
var Group = (props) => {
  const {
    children,
    className,
    cx,
    theme,
    getStyles,
    Heading,
    headingProps,
    label,
    selectProps,
    innerProps,
    getClassNames
  } = props;
  const { chakraStyles } = selectProps;
  const initialSx = {};
  const sx = (chakraStyles == null ? void 0 : chakraStyles.group) ? chakraStyles.group(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsxs(Box, { ...innerProps, className: cx({ group: true }, className), sx, children: [
    /* @__PURE__ */ jsx(
      Heading,
      {
        ...headingProps,
        selectProps,
        cx,
        theme,
        getStyles,
        getClassNames,
        children: label
      }
    ),
    /* @__PURE__ */ jsx(Box, { children })
  ] });
};
var GroupHeading = (props) => {
  const {
    cx,
    className,
    selectProps: { chakraStyles, size: sizeProp }
  } = props;
  const { data, ...innerProps } = cleanCommonProps(props);
  const menuStyles = useMultiStyleConfig("Menu");
  const size = useSize(sizeProp);
  const fontSizes = {
    sm: "xs",
    md: "sm",
    lg: "md"
  };
  const paddings = {
    sm: "0.4rem 0.8rem",
    md: "0.5rem 1rem",
    lg: "0.6rem 1.2rem"
  };
  const initialSx = {
    ...menuStyles.groupTitle,
    fontSize: fontSizes[size],
    padding: paddings[size],
    margin: 0
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.groupHeading) ? chakraStyles.groupHeading(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx({ "group-heading": true }, className),
      sx
    }
  );
};
var CheckIcon = (props) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 14 14", width: "1em", height: "1em", ...props, children: /* @__PURE__ */ jsx(
  "polygon",
  {
    fill: "currentColor",
    points: "5.5 11.9993304 14 3.49933039 12.5 2 5.5 8.99933039 1.5 4.9968652 0 6.49933039"
  }
) });
var Option = (props) => {
  const {
    className,
    cx,
    innerRef,
    innerProps,
    children,
    isFocused,
    isDisabled,
    isSelected,
    selectProps: {
      chakraStyles,
      size: sizeProp,
      isMulti,
      hideSelectedOptions,
      selectedOptionStyle,
      selectedOptionColorScheme
    }
  } = props;
  const menuItemStyles = useMultiStyleConfig("Menu").item;
  const size = useSize(sizeProp);
  const horizontalPaddingOptions = {
    sm: "0.6rem",
    md: "0.8rem",
    lg: "1rem"
  };
  const verticalPaddingOptions = {
    sm: "0.3rem",
    md: "0.4rem",
    lg: "0.5rem"
  };
  const selectedBg = useColorModeValue(
    `${selectedOptionColorScheme}.500`,
    `${selectedOptionColorScheme}.300`
  );
  const selectedColor = useColorModeValue("white", "black");
  const showCheckIcon = selectedOptionStyle === "check" && (!isMulti || hideSelectedOptions === false);
  const shouldHighlight = selectedOptionStyle === "color" && isSelected;
  const initialSx = {
    ...menuItemStyles,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    width: "100%",
    textAlign: "start",
    fontSize: size,
    paddingX: horizontalPaddingOptions[size],
    paddingY: verticalPaddingOptions[size],
    ...shouldHighlight ? {
      bg: selectedBg,
      color: selectedColor,
      _active: { bg: selectedBg }
    } : {}
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.option) ? chakraStyles.option(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsxs(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          option: true,
          "option--is-disabled": isDisabled,
          "option--is-focused": isFocused,
          "option--is-selected": isSelected
        },
        className
      ),
      sx,
      ref: innerRef,
      "data-focus": isFocused ? true : void 0,
      "aria-disabled": isDisabled ? true : void 0,
      "aria-selected": isSelected,
      children: [
        showCheckIcon && /* @__PURE__ */ jsx(
          MenuIcon,
          {
            fontSize: "0.8em",
            marginEnd: "0.75rem",
            opacity: isSelected ? 1 : 0,
            children: /* @__PURE__ */ jsx(CheckIcon, {})
          }
        ),
        children
      ]
    }
  );
};
var hasColorScheme = (option) => typeof option === "object" && option !== null && "colorScheme" in option && typeof option.colorScheme === "string";
var hasVariant = (option) => typeof option === "object" && option !== null && "variant" in option && typeof option.variant === "string";
var MultiValue = (props) => {
  const {
    children,
    className,
    components,
    cx,
    data,
    innerProps,
    isDisabled,
    isFocused,
    removeProps,
    selectProps,
    cropWithEllipsis
  } = props;
  const { Container, Label, Remove } = components;
  const {
    chakraStyles,
    tagColorScheme,
    tagVariant,
    size: sizeProp
  } = selectProps;
  const size = useSize(sizeProp);
  let optionColorScheme = "";
  let optionVariant = "";
  if (hasColorScheme(data)) {
    optionColorScheme = data.colorScheme;
  }
  if (hasVariant(data)) {
    optionVariant = data.variant;
  }
  const tagStyles = useMultiStyleConfig("Tag", {
    size,
    colorScheme: optionColorScheme || tagColorScheme,
    variant: optionVariant || tagVariant
  });
  const containerInitialSx = {
    ...tagStyles.container,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    // resolves flex/text-overflow bug
    margin: "0.125rem"
  };
  const containerSx = (chakraStyles == null ? void 0 : chakraStyles.multiValue) ? chakraStyles.multiValue(containerInitialSx, props) : containerInitialSx;
  const labelInitialSx = {
    ...tagStyles.label,
    overflow: "hidden",
    textOverflow: cropWithEllipsis || cropWithEllipsis === void 0 ? "ellipsis" : void 0,
    whiteSpace: "nowrap"
  };
  const labelSx = (chakraStyles == null ? void 0 : chakraStyles.multiValueLabel) ? chakraStyles.multiValueLabel(labelInitialSx, props) : labelInitialSx;
  const removeInitialSx = {
    ...tagStyles.closeButton,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };
  const removeSx = (chakraStyles == null ? void 0 : chakraStyles.multiValueRemove) ? chakraStyles.multiValueRemove(removeInitialSx, props) : removeInitialSx;
  return /* @__PURE__ */ jsxs(
    Container,
    {
      data,
      innerProps: {
        className: cx(
          {
            "multi-value": true,
            "multi-value--is-disabled": isDisabled
          },
          className
        ),
        ...innerProps
      },
      sx: containerSx,
      selectProps,
      children: [
        /* @__PURE__ */ jsx(
          Label,
          {
            data,
            innerProps: {
              className: cx(
                {
                  "multi-value__label": true
                },
                className
              )
            },
            sx: labelSx,
            selectProps,
            children
          }
        ),
        /* @__PURE__ */ jsx(
          Remove,
          {
            data,
            innerProps: {
              className: cx(
                {
                  "multi-value__remove": true
                },
                className
              ),
              "aria-label": `Remove ${children || "option"}`,
              ...removeProps
            },
            sx: removeSx,
            selectProps,
            isFocused
          }
        )
      ]
    }
  );
};
var MultiValueContainer = (props) => {
  const { children, innerProps, sx } = props;
  return /* @__PURE__ */ jsx(chakra.span, { ...innerProps, sx, children });
};
var MultiValueLabel = (props) => {
  const { children, innerProps, sx } = props;
  return /* @__PURE__ */ jsx(chakra.span, { ...innerProps, sx, children });
};
var TagCloseIcon = (props) => /* @__PURE__ */ jsx(Icon, { verticalAlign: "inherit", viewBox: "0 0 512 512", ...props, children: /* @__PURE__ */ jsx(
  "path",
  {
    fill: "currentColor",
    d: "M289.94 256l95-95A24 24 0 00351 127l-95 95-95-95a24 24 0 00-34 34l95 95-95 95a24 24 0 1034 34l95-95 95 95a24 24 0 0034-34z"
  }
) });
var MultiValueRemove = (props) => {
  const { children, innerProps, isFocused, sx } = props;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      role: "button",
      sx,
      "data-focus": isFocused ? true : void 0,
      "data-focus-visible": isFocused ? true : void 0,
      children: children || /* @__PURE__ */ jsx(TagCloseIcon, {})
    }
  );
};
var Placeholder = (props) => {
  const {
    children,
    className,
    cx,
    innerProps,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    gridArea: "1 / 1 / 2 / 3",
    color: "chakra-placeholder-color",
    mx: "0.125rem",
    userSelect: "none"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.placeholder) ? chakraStyles.placeholder(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...innerProps,
      className: cx(
        {
          placeholder: true
        },
        className
      ),
      sx,
      children
    }
  );
};
var SingleValue = (props) => {
  const {
    children,
    className,
    cx,
    isDisabled,
    innerProps,
    selectProps: { chakraStyles }
  } = props;
  const initialSx = {
    gridArea: "1 / 1 / 2 / 3",
    mx: "0.125rem",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  };
  const sx = (chakraStyles == null ? void 0 : chakraStyles.singleValue) ? chakraStyles.singleValue(initialSx, props) : initialSx;
  return /* @__PURE__ */ jsx(
    Box,
    {
      className: cx(
        {
          "single-value": true,
          "single-value--is-disabled": isDisabled
        },
        className
      ),
      sx,
      ...innerProps,
      children
    }
  );
};

// src/chakra-components/index.ts
var chakraComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorSeparator,
  IndicatorsContainer,
  Input,
  LoadingIndicator,
  LoadingMessage,
  Menu,
  MenuList,
  MultiValue,
  MultiValueContainer,
  MultiValueLabel,
  MultiValueRemove,
  NoOptionsMessage,
  Option,
  Placeholder,
  SelectContainer,
  SingleValue,
  ValueContainer
};
var chakra_components_default = chakraComponents;

// src/use-chakra-select-props.ts
var useChakraSelectProps = ({
  components = {},
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  theme,
  size,
  isDisabled,
  isInvalid,
  isReadOnly,
  required,
  isRequired,
  inputId,
  selectedOptionStyle = "color",
  selectedOptionColorScheme = "blue",
  variant,
  tagColorScheme,
  tagVariant,
  focusBorderColor,
  errorBorderColor,
  chakraStyles = {},
  onFocus,
  onBlur,
  menuIsOpen,
  ...props
}) => {
  var _a, _b, _c, _d, _e, _f, _g;
  const chakraTheme = useTheme();
  const { variant: defaultVariant = "outline" } = (_c = (_b = (_a = chakraTheme == null ? void 0 : chakraTheme.components) == null ? void 0 : _a.Input) == null ? void 0 : _b.defaultProps) != null ? _c : {};
  const {
    colorScheme: defaultTagColorScheme = "gray",
    variant: defaultTagVariant = "subtle"
  } = (_f = (_e = (_d = chakraTheme == null ? void 0 : chakraTheme.components) == null ? void 0 : _d.Tag) == null ? void 0 : _e.defaultProps) != null ? _f : {};
  const inputProps = useFormControl({
    id: inputId,
    isDisabled,
    isInvalid,
    isRequired,
    isReadOnly,
    onFocus,
    onBlur
  });
  const realMenuIsOpen = menuIsOpen != null ? menuIsOpen : inputProps.readOnly ? false : void 0;
  let realSelectedOptionStyle = selectedOptionStyle;
  const selectedOptionStyleOptions = ["color", "check"];
  if (!selectedOptionStyleOptions.includes(selectedOptionStyle)) {
    realSelectedOptionStyle = "color";
  }
  let realSelectedOptionColorScheme = selectedOptionColorScheme;
  if (typeof realSelectedOptionColorScheme !== "string") {
    realSelectedOptionColorScheme = "blue";
  }
  const selectProps = {
    // Allow overriding of custom components
    components: {
      ...chakra_components_default,
      ...components
    },
    // Custom select props
    size,
    selectedOptionStyle: realSelectedOptionStyle,
    selectedOptionColorScheme: realSelectedOptionColorScheme,
    variant: variant != null ? variant : defaultVariant,
    tagColorScheme: tagColorScheme != null ? tagColorScheme : defaultTagColorScheme,
    tagVariant: tagVariant != null ? tagVariant : defaultTagVariant,
    chakraStyles,
    focusBorderColor,
    errorBorderColor,
    // Extract custom props from form control
    onFocus: inputProps.onFocus,
    onBlur: inputProps.onBlur,
    isDisabled: inputProps.disabled,
    isInvalid: !!inputProps["aria-invalid"],
    inputId: inputProps.id,
    isReadOnly: inputProps.readOnly,
    required: required != null ? required : inputProps.required,
    menuIsOpen: realMenuIsOpen,
    ...props,
    // aria-invalid can be passed to react-select, so we allow that to
    // override the `isInvalid` prop
    "aria-invalid": (_g = props["aria-invalid"]) != null ? _g : inputProps["aria-invalid"]
  };
  return selectProps;
};
var use_chakra_select_props_default = useChakraSelectProps;
var Select = forwardRef(
  (props, ref) => {
    const chakraSelectProps = use_chakra_select_props_default(props);
    return /* @__PURE__ */ jsx(ReactSelect__default, { ref, ...chakraSelectProps });
  }
);
var select_default = Select;
var CreatableSelect = forwardRef(
  (props, ref) => {
    const chakraSelectProps = use_chakra_select_props_default(props);
    return /* @__PURE__ */ jsx(CreatableReactSelect, { ref, ...chakraSelectProps });
  }
);
var creatable_select_default = CreatableSelect;
var AsyncSelect = forwardRef(
  (props, ref) => {
    const chakraSelectProps = use_chakra_select_props_default(props);
    return /* @__PURE__ */ jsx(AsyncReactSelect, { ref, ...chakraSelectProps });
  }
);
var async_select_default = AsyncSelect;
var AsyncCreatableSelect = forwardRef(
  (props, ref) => {
    const chakraSelectProps = use_chakra_select_props_default(props);
    return /* @__PURE__ */ jsx(AsyncCreatableReactSelect, { ref, ...chakraSelectProps });
  }
);
var async_creatable_select_default = AsyncCreatableSelect;

export { async_creatable_select_default as AsyncCreatableSelect, async_select_default as AsyncSelect, creatable_select_default as CreatableSelect, select_default as Select, chakra_components_default as chakraComponents, use_chakra_select_props_default as useChakraSelectProps };
